import { atom } from '@reatom/framework'

import { AvatarsHost, TokenLogosHost } from './models'
import { TokenBannersHost } from './models'

export type AssetBuckets = {
  tokenLogosHost: TokenLogosHost
  tokenBannersHost: TokenBannersHost
  commentAttachmentsHost: string
  avatarsHost: AvatarsHost
  customEmojiHost: string
  customEmojiStaticHost: string

  tempImagesHost: string
}

export type Environment = {
  tradeApiHost: string
  pathfinderApiHost: string
  pathfinderEnabled: boolean
  apiHost: string
  webUrl: string
  isDev: boolean
  latestReleaseUrl: string
  checkUpdatePeriod: string
  packageVersion: string
  applinkScheme: string
  supportAccount: string
  walletsPollingInterval: number
  nativePricesPollingInterval: number
  gasPricesRefreshInterval: number
  quoteRefreshInterval: number
  topGainersPollingIntervalSeconds: number
  directLink: string
  downloadUrl: string
  assets: AssetBuckets
  orderChangeFeeSvm: number
  transferFeeSvm: number
  buybotUsername: string
  premium: {
    boosterPriceSol: number
    boosterPriceEth: number
    treasurySvm: string
    treasuryEvm: string
    premiumBoosters: number
    premiumExpirationDays: number
    extendPremiumBoosters: number
    minimumBoostersPurchase: number
  }
  policiesHost: string
  sentryDsn: string
}

export const TRADE_API_URL = import.meta.env.VITE_TRADE_API_URL
const API_URL = import.meta.env.VITE_API_URL || ''

/** Chart WebSocket host base URL (no trailing slash). Optional when chart features are unused. */
export const CHART_BACKEND_URL =
  `${import.meta.env.VITE_CHART_BACKEND_URL ?? ''}`.replace(/\/$/, '')

/**
 * Pathfinder routing API base URL. Empty string disables pathfinder
 * integration; routing falls back to the existing Rust-side swap builder.
 */
export const PATHFINDER_API_URL: string =
  import.meta.env.VITE_PATHFINDER_API_URL || ''
const PATHFINDER_ENABLED = import.meta.env.VITE_PATHFINDER_ENABLED === 'true'
const WEB_URL = import.meta.env.VITE_WEB_URL
const LATEST_RELEASE_URL = import.meta.env.VITE_LATEST_RELEASE_URL
const PACKAGE_VERSION = import.meta.env.VITE_PACKAGE_VERSION
const APPLINK_SCHEME = import.meta.env.VITE_APPLINK_SCHEME
const BUCKET_URL = import.meta.env.VITE_BUCKET_URL
const SUPPORT_ACCOUNT = import.meta.env.VITE_SUPPORT_ACCOUNT
const WALLETS_POLL_INTERVAL = import.meta.env.VITE_WALLETS_POLL_INTERVAL_SECONDS
const NATIVE_PRICES_POLL_INTERVAL_SECONDS = import.meta.env
  .VITE_NATIVE_PRICES_POLL_INTERVAL_SECONDS
const QUOTE_REFRESH_INTERVAL_SECONDS = import.meta.env
  .VITE_QUOTE_REFRESH_INTERVAL_SECONDS
const TOP_GAINERS_POLLING_INTERVAL_SECONDS = import.meta.env
  .VITE_TOP_GAINERS_POLLING_INTERVAL_SECONDS
const GAS_PRICES_REFRESH_INTERVAL_SECONDS = import.meta.env
  .VITE_GAS_PRICES_REFRESH_INTERVAL_SECONDS
const DIRECT_LINK = import.meta.env.VITE_DIRECT_LINK
const DOWNLOAD_URL = import.meta.env.VITE_DOWNLOAD_URL

const BOOSTER_PRICE_ETH = import.meta.env.VITE_BOOSTER_PRICE_ETH
const BOOSTER_PRICE_SOL = import.meta.env.VITE_BOOSTER_PRICE_SOL
const PREMIUM_BOOSTERS = import.meta.env.VITE_PREMIUM_BOOSTERS
const PREMIUM_EXPIRATION_DAYS = import.meta.env.VITE_PREMIUM_EXPIRATION_DAYS
const EXTEND_PREMIUM_BOOSTERS = import.meta.env.VITE_EXTEND_PREMIUM_BOOSTERS
const TREASURY_SVM = import.meta.env.VITE_TREASURY_SVM
const TREASURY_EVM = import.meta.env.VITE_TREASURY_EVM
const ORDER_CHANGE_FEE_SVM = import.meta.env.VITE_ORDER_CHANGE_FEE_SVM
const VITE_TRANSFER_FEE_SVM = import.meta.env.VITE_TRANSFER_FEE_SVM
const BUYBOT_USERNAME = import.meta.env.VITE_BUYBOT_USERNAME
const CHECK_UPDATE_PERIOD_MILLIS = import.meta.env
  .VITE_CHECK_UPDATE_PERIOD_MILLIS
export const FUN_ENABLED = import.meta.env.VITE_FUN_ENABLED === 'true'
/**
 * Premium — paid token boosts and the premium feed. Hidden until kol.trade
 * sells it: every entry point checks this, so nothing offers a purchase that
 * cannot be completed.
 */
export const PREMIUM_ENABLED = import.meta.env.VITE_PREMIUM_ENABLED === 'true'
/**
 * Whether the market pages — the feed and a token — are open to a visitor
 * with no account at all.
 *
 * Off unless the build says otherwise, so a build that forgets to mention it
 * stays invite-only. It is set on staging and not on production: staging is
 * where the thing can be shown to somebody without first admitting them, and
 * production keeps its door. Everything that is not a market page is
 * unaffected and still sends people to the bot.
 */
export const PUBLIC_MARKET_PAGES =
  import.meta.env.VITE_PUBLIC_MARKET_PAGES === 'true'
export const POLICIES_HOST = import.meta.env.VITE_POLICIES_HOST

export const DEMO_MODE_STORAGE_KEY = 'demoMode'
export const DEMO_MODE =
  import.meta.env.VITE_DEMO_MODE === 'true' ||
  localStorage.getItem(DEMO_MODE_STORAGE_KEY) === 'true'
export const APP_MODE_PRODUCTION = import.meta.env.MODE === 'production'
export const TOKEN_BANNERS_HOST = `${BUCKET_URL}/token-banners`

export const LOG_API_ENABLED =
  import.meta.env.VITE_LOG_API_ENABLED === 'true' ||
  localStorage.getItem('LOG_API_ENABLED') === 'true'

export const LOG_WS_EMITS_ENABLED =
  import.meta.env.VITE_LOG_WS_EMITS === 'true' ||
  localStorage.getItem('LOG_WS_EMITS') === 'true'

export const LOG_WS_EVENTS_ENABLED =
  import.meta.env.VITE_LOG_WS_EVENTS === 'true' ||
  localStorage.getItem('LOG_WS_EVENTS') === 'true'

export const LOG_TAURI_INVOKE_ENABLED =
  import.meta.env.VITE_LOG_TAURI_INVOKE_ENABLED === 'true' ||
  localStorage.getItem('LOG_TAURI_INVOKE_ENABLED') === 'true'

export const SKIP_EXPLORE_CLIENT_FILTERS =
  import.meta.env.VITE_SKIP_EXPLORE_CLIENT_FILTERS === 'true'

export const environment: Environment = {
  tradeApiHost: `${TRADE_API_URL}`,
  pathfinderApiHost: `${PATHFINDER_API_URL}`,
  apiHost: API_URL,
  isDev: import.meta.env.DEV,
  policiesHost: POLICIES_HOST,
  webUrl: WEB_URL,
  latestReleaseUrl: LATEST_RELEASE_URL,
  checkUpdatePeriod: CHECK_UPDATE_PERIOD_MILLIS,
  packageVersion: PACKAGE_VERSION,
  applinkScheme: APPLINK_SCHEME,
  supportAccount: SUPPORT_ACCOUNT,
  walletsPollingInterval: Number(WALLETS_POLL_INTERVAL) || 30,
  nativePricesPollingInterval:
    Number(NATIVE_PRICES_POLL_INTERVAL_SECONDS) || 30,
  gasPricesRefreshInterval: Number(GAS_PRICES_REFRESH_INTERVAL_SECONDS) || 20,
  quoteRefreshInterval: Number(QUOTE_REFRESH_INTERVAL_SECONDS) || 30,
  topGainersPollingIntervalSeconds:
    Number(TOP_GAINERS_POLLING_INTERVAL_SECONDS) || 30,
  directLink: DIRECT_LINK,
  downloadUrl: DOWNLOAD_URL,
  orderChangeFeeSvm: Number(ORDER_CHANGE_FEE_SVM),
  transferFeeSvm: Number(VITE_TRANSFER_FEE_SVM),
  buybotUsername: BUYBOT_USERNAME,
  assets: {
    tokenLogosHost: TokenLogosHost(`${BUCKET_URL}/token-logos`),
    tokenBannersHost: TokenBannersHost(TOKEN_BANNERS_HOST),
    avatarsHost: AvatarsHost(`${BUCKET_URL}/avatars`),
    customEmojiHost: `${BUCKET_URL}/custom-emoji-webm`,
    customEmojiStaticHost: `${BUCKET_URL}/custom-emoji-png`,
    tempImagesHost: `${BUCKET_URL}/temp-images`,
    commentAttachmentsHost: `${BUCKET_URL}/comment-attachments`,
  },
  premium: {
    boosterPriceEth: Number(BOOSTER_PRICE_ETH) || 0.001,
    boosterPriceSol: Number(BOOSTER_PRICE_SOL) || 0.068,
    premiumBoosters: Number(PREMIUM_BOOSTERS) || 100,
    premiumExpirationDays: Number(PREMIUM_EXPIRATION_DAYS) || 30,
    treasurySvm: TREASURY_SVM || 'DLGfkAmsKm9o6bQeUXoxtAywfwH4Q11EpYbGFkn579m5',
    treasuryEvm: TREASURY_EVM || '0x00000',
    extendPremiumBoosters: Number(EXTEND_PREMIUM_BOOSTERS) || 10,
    minimumBoostersPurchase: 100,
  },
  sentryDsn: import.meta.env.VITE_SENTRY_DSN,
  pathfinderEnabled: PATHFINDER_ENABLED,
}

export const environmentAtom = atom(() => {
  return environment
}, 'environmentAtom')
